import {GoogleMap, Libraries, Marker, Polygon, useJsApiLoader} from "@react-google-maps/api";
import {useCallback, useEffect, useState} from "react";
import { Button, Checkbox, Stack, LinearProgress } from "@mui/material";
import { GridOverlay } from "./GridOverlay";
import { isPointInConvexPolygon } from "../../utils/MapUtils";
import {Coordinates, getCoordinates, persistAddress} from "../../service/map.service";
import { useAuthStore } from "../../store/auth.store";
import { TextInput } from "../form/TextInput";
import { enqueueSnackbar } from "notistack";


interface MarkerProp {
    lat: number
    lng: number
}

interface MapProps {
    marker?: MarkerProp
    polygon?: {lat: number, lng: number }[],
    onPolygonChange?: (polygon:{lat: number, lng: number }[]) => void,
    setMarker?: (m:MarkerProp) => void
    version?:number
    onLocationSelect?: (position: { address: string }) => void;
    onClose?: (reason?:string) => void;
    autoSelectLocation?: boolean;
    loading?: boolean;
    externalLoading?: (b:boolean) => void;
}

const containerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "12px",
};
export const DEFAULT_LAT = 41.640880319095665;
export const DEFAULT_LNG = 41.614235657432886;


const API_KEY = process.env.REACT_APP_GMAPS_API_KEY;
                    const libraries:Libraries = ['geometry']
export function Map({externalLoading, polygon, onPolygonChange, autoSelectLocation, loading, marker, setMarker, onLocationSelect, onClose, version }: MapProps) {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: API_KEY!,
        libraries: libraries
    });

    const {can} = useAuthStore();
    const [localVersion, setLocalVersion] = useState<number>();
    const [foundCoordinate, setFoundCoordinate] = useState<Coordinates>();
    const [mapAdmin, setMapAdmin] = useState(false);
    const [coordinatesLoading, _setCoordinatesLoading] = useState(false);

    function setCoordinatesLoading(loading: boolean) {
        if(externalLoading) {
            setCoordinatesLoading(loading);
        } else {
            _setCoordinatesLoading(loading);
        }
    }

    const stepLat = 0.00015;
    const stepLng = 0.0002;

    const [selectedAddress, setSelectedAddress] = useState<string>();

    const [centerMarker, setCenterMarker] = useState<{ lat: number, lng: number } | undefined>(normalizeMarker(marker));
    const [map, setMap] = useState<google.maps.Map | null>(null);

    const saveAddress = useCallback(function() {
        persistAddress({
            lat: marker!.lat,
            lng: marker!.lng,
            address: foundCoordinate!.address!,
            hint: 'manual'
        }).then((a) => {
            setFoundCoordinate(a.data)
        }).catch(e => enqueueSnackbar({variant: "error", message: "Failed to persist address: " + e.message}))
    }, [foundCoordinate, marker]                                                       )

    useEffect(() => {
        const callback = (e:{key: string}) => {
            if(mapAdmin && e.key === 'Enter') {
                saveAddress()
            }
        }
        document.addEventListener('keyup', callback)
        return () => document.removeEventListener('keyup', callback);
    }, [saveAddress, mapAdmin]);

    useEffect(() => {
        if(version !== localVersion) {
            setCenterMarker(marker);
            setLocalVersion(version);
        }
    }, [version, localVersion, marker]);

    const handleClick = useCallback(
        (event: google.maps.MapMouseEvent) => {
            if(onPolygonChange && event.latLng) {
                onPolygonChange([...(polygon || []), {lat: event.latLng.lat(), lng: event.latLng.lng()}])
            }
            if (onLocationSelect && event.latLng) {
                const lat = event.latLng.lat();
                const lng = event.latLng.lng();
                setCoordinatesLoading(true)
                setMarker && setMarker({ lat, lng });
                getCoordinates(lat, lng)
                    .then(a => {
                        const address = a.data[0].address;
                        setSelectedAddress(address)
                        if(a.data[0].address){
                            setFoundCoordinate(a.data[0]);
                        }
                        if(autoSelectLocation) {
                            onLocationSelect({address: address || ""})
                        }
                    })
                    .catch(() => {
                        setSelectedAddress(undefined)
                        setFoundCoordinate(undefined);
                        if(autoSelectLocation) {
                            onLocationSelect({address: ""})
                        }
                    })
                    .finally(() => setCoordinatesLoading(false))

                if(!mapAdmin) {
                    setTimeout(() => setCenterMarker({lat, lng}), 500);
                }
                if (map && !mapAdmin) {
                    map.panTo({ lat, lng });
                }
            }
        },
        [onLocationSelect, autoSelectLocation, map, mapAdmin, setMarker, polygon, onPolygonChange]
    );

    const innerPolygon = [
        { lat: 41.658741628399426, lng: 41.641273498535156 },
        { lat: 41.663738112055704, lng: 41.678894735018254 },
        { lat: 41.65399104436312, lng: 41.692627645174504 },
        { lat: 41.60292310022463, lng: 41.60851357046747 },
        { lat: 41.61797429813666, lng: 41.58073376043459 },
    ];

    if (!isLoaded) return <p>Loading map…</p>;

    return (
        <Stack gap={"15px"}>
            <Stack>
                <GoogleMap
                    mapContainerClassName={"google-map-loader-container"}
                    mapContainerStyle={containerStyle}
                    center={centerMarker && centerMarker.lat !== undefined && centerMarker.lng !== undefined ? centerMarker : { lat: DEFAULT_LAT, lng: DEFAULT_LNG }}
                    onClick={handleClick}
                    onLoad={(mapInstance) => setMap(mapInstance)}
                    zoom={14}
                    options={{
                        disableDefaultUI: false,
                        clickableIcons: false,
                        styles:  [
                            {
                                featureType: "poi",
                                stylers: [{ visibility: "off" }],
                            },
                            {
                                featureType: "poi.business",
                                stylers: [{ visibility: "off" }],
                            },
                            {
                                featureType: "poi.park",
                                stylers: [{ visibility: "off" }],
                            },
                        ]}}
                >
                    {marker && marker.lat !== undefined && marker.lng !== undefined  && <Marker position={marker}/>}
                    {map && <GridOverlay map={map}
                                         marker={foundCoordinate}
                                         mapAdmin={mapAdmin}
                                         stepLat={stepLat}
                                         stepLng={stepLng}
                    />}
                    {polygon && <Polygon
                        paths={polygon}
                        options={{
                            strokeColor: '#FF0000',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: '#FF0000',
                            fillOpacity: 0.35,
                        }}
                    />}
                    {polygon && polygon.map((point, index) => (
                        <Marker key={index}
                                position={point}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    scale: 6, // radius of the circle
                                    fillColor: "red",
                                    fillOpacity: 1,
                                    strokeWeight: 1,
                                    strokeColor: "white"
                                }}/>
                    ))})
                </GoogleMap>
                {!externalLoading && <LinearProgress variant={loading || coordinatesLoading ? "indeterminate" : "determinate" } value={100}/>}
            </Stack>
            {can('full_access') && <Stack alignItems={"center"} flexDirection={"row"}>
                <Checkbox checked={mapAdmin} onChange={() => setMapAdmin(!mapAdmin)} />
                Map Admin Mode
            </Stack>}
            {mapAdmin && marker && <div>lat: {Math.round(marker!.lat * 100000) / 100000} lng: {Math.round(marker!.lng * 100000) / 100000} in poly: {isPointInConvexPolygon(marker, innerPolygon) ? "true" : "false"}</div>}
            {mapAdmin && <Stack flexDirection={"row"} gap={"15px"}>
                <TextInput
                    sx={{flex: "1 0 auto"}}
                    label={"Marker Location Address"}
                    value={foundCoordinate?.address || ""}
                    onChange={e => setFoundCoordinate({...foundCoordinate!, address: e.target.value})}
                    onKeyUp={(e) => {
                        if(e.key === 'Enter') {
                            saveAddress();
                        }
                }}/>
                <Button variant={"contained"} onClick={() => saveAddress()}>Update marker address</Button>
            </Stack>}
            {/*@ts-ignore*/}
            {(onLocationSelect && !autoSelectLocation) && selectedAddress && <div>Address: {selectedAddress}</div>}
            {((onLocationSelect && !autoSelectLocation) || onClose) && <Stack flexDirection={"row"} justifyContent={"space-between"}>
                {onClose && <Button onClick={() => onClose()}>Close</Button>}
                {(onLocationSelect && !autoSelectLocation) && <Button disabled={!marker || !selectedAddress} onClick={() => {
                    onLocationSelect({address: selectedAddress!})
                    onClose && onClose()
                }}>Select Location</Button>}
                {onPolygonChange && <Button disabled={!polygon || polygon.length === 0} onClick={() => {
                    onPolygonChange([])
                }}>Clear</Button>}
                {onPolygonChange && <Button disabled={!polygon || polygon.length < 3} onClick={() => {
                    onClose && onClose("apply_polygon")
                }}>Apply Area</Button>}
            </Stack>}
        </Stack>
    );
}



function normalizeMarker(marker?:MarkerProp):MarkerProp {
    if(!marker) return {lat: DEFAULT_LAT, lng: DEFAULT_LNG};
    if(marker.lat === undefined || marker.lng === undefined) {
        return {
            lat: DEFAULT_LAT,
            lng:DEFAULT_LNG,
        };
    }
    return marker
}